/**
 * Frontend resolver for Google Product Taxonomy IDs.
 *
 * Lazy-loads the 946KB taxonomy file on first use, caches in memory.
 * Resolves numeric GPC IDs (e.g. "1604") to localized text paths
 * (e.g. "Bekleidung > Kleidung > Kleider" / "Apparel & Accessories > Clothing > Dresses").
 *
 * The backend stores GPC as numeric IDs for language-neutral GMC uploads.
 * The frontend uses this resolver to display both the ID and the text path.
 */

let _taxonomy: Record<string, [string, string]> | null = null;
let _loading: Promise<Record<string, [string, string]>> | null = null;

async function loadTaxonomy(): Promise<Record<string, [string, string]>> {
  if (_taxonomy) return _taxonomy;
  if (_loading) return _loading;

  _loading = fetch('/gpc-taxonomy.json')
    .then((r) => {
      if (!r.ok) throw new Error(`Failed to load taxonomy: ${r.status}`);
      return r.json();
    })
    .then((data: Record<string, [string, string]>) => {
      _taxonomy = data;
      _loading = null;
      return data;
    })
    .catch((err) => {
      _loading = null;
      throw err;
    });

  return _loading;
}

export interface GpcDisplay {
  id: string | null;
  text: string | null;
}

/**
 * Resolve a stored GPC value to display form.
 * - Numeric ID (e.g. "1604") → { id: "1604", text: localized path from taxonomy }
 * - Legacy text path → { id: null, text: value }
 * - null/empty → { id: null, text: null }
 */
export async function resolveGpc(
  value: string | null | undefined,
  lang: 'en' | 'de' = 'de',
): Promise<GpcDisplay> {
  if (!value || typeof value !== 'string') return { id: null, text: null };
  const trimmed = value.trim();
  if (!trimmed) return { id: null, text: null };

  if (/^\d+$/.test(trimmed)) {
    try {
      const tax = await loadTaxonomy();
      const entry = tax[trimmed];
      return { id: trimmed, text: entry?.[lang === 'de' ? 1 : 0] ?? null };
    } catch {
      return { id: trimmed, text: null };
    }
  }

  // Legacy text path — return as-is
  return { id: null, text: trimmed };
}

/**
 * Synchronous resolve — only works if taxonomy is already loaded.
 * Returns null if not loaded. Use for performance-critical code where
 * an async resolve is problematic.
 */
export function resolveGpcSync(
  value: string | null | undefined,
  lang: 'en' | 'de' = 'de',
): GpcDisplay {
  if (!value || typeof value !== 'string') return { id: null, text: null };
  const trimmed = value.trim();
  if (!trimmed) return { id: null, text: null };

  if (/^\d+$/.test(trimmed)) {
    if (!_taxonomy) return { id: trimmed, text: null };
    const entry = _taxonomy[trimmed];
    return { id: trimmed, text: entry?.[lang === 'de' ? 1 : 0] ?? null };
  }

  return { id: null, text: trimmed };
}

/**
 * Preload the taxonomy. Call this early in app startup if you know
 * you'll need GPC resolution soon (e.g. on the Workbench page).
 */
export function preloadTaxonomy(): Promise<void> {
  return loadTaxonomy().then(() => undefined);
}
